import { Injectable, inject, signal, computed, effect, PLATFORM_ID } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { LocalStorageService } from '../local-storage/local-storage.service';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private _localStorage = inject(LocalStorageService);
  private _document = inject(DOCUMENT);
  private _platformId = inject(PLATFORM_ID);

  private _theme = signal<'light' | 'dark'>('light');

  readonly theme = this._theme.asReadonly();
  readonly isDarkMode = computed(() => this._theme() === 'dark');

  constructor() {
    this._initTheme();

    effect(() => {
      const current = this._theme();
      if (isPlatformBrowser(this._platformId)) {
        this._document.documentElement.setAttribute('data-theme', current);
        this._localStorage.setItem('theme', current);
      }
    });
  }

  private _initTheme(): void {
    if (isPlatformBrowser(this._platformId)) {
      const saved = this._localStorage.getItem('theme') as 'light' | 'dark';

      if (!saved) {
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        this._theme.set(prefersDark ? 'dark' : 'light');
      } else {
        this._theme.set(saved);
      }
    }
  }

  toggleTheme(): void {
    this._theme.update((prev) => (prev === 'light' ? 'dark' : 'light'));
  }
}
